import get from 'lodash/get';
import vuetifyColors from 'vuetify/lib/util/colors';

function determineValue(determination, values) {
  if (!values || values.length === 0) {
    return null;
  }

  const validValues = values.filter((value) => typeof value === 'number' && !isNaN(value) && isFinite(value));

  if (validValues.length === 0) {
    return null;
  }

  let determinedValue = null;
  switch (determination) {
    case 'min':
      determinedValue = Math.min(...validValues);
      break;
    case 'average':
      determinedValue = validValues.reduce((sum, value) => sum + value, 0) / validValues.length;
      break;
    case 'sum':
      determinedValue = validValues.reduce((sum, value) => sum + value, 0);
      break;
    case 'earliest':
      for (let i = 0; i < validValues.length; i++) {
        determinedValue = validValues[i];
        break;
      }
      break;
    case 'latest':
      for (let i = validValues.length - 1; i >= 0; i--) {
        determinedValue = validValues[i];
        break;
      }
      break;
    case 'max':
    default:
      determinedValue = Math.max(...validValues);
      break;
  }

  return determinedValue;
}

export default {
  name: 'ChartGaugeMixin',
  methods: {
    getGaugeChartDataSets() {
      if (this.chartType !== 'gauge') {
        return [];
      }

      let pointerColors = Object.entries(vuetifyColors)
        .map(([colorName, colorVariants]) => {
          return { color: colorVariants.base, name: colorName };
        })
        .filter((item) => {
          const unwanted = ['shades', 'grey'];
          return !unwanted.includes(item.name);
        });
      pointerColors = pointerColors.sort((a, b) => a.name.localeCompare(b.name));
      pointerColors = pointerColors.map((color) => color.color);

      let stateIndex = -1;
      let dataSets = [];
      this.chartData.forEach((twin) => {
        twin.states.forEach((state) => {
          const determination = get(this.widget.chartOptions, 'gaugeChart.valueDetermination', 'max');

          const nonNullHistoryValues = state.history
            .filter((h) => h.value !== null && h.value !== undefined)
            .sort((a, b) => (new Date(a.timestamp) < new Date(b.timestamp) ? -1 : 1))
            .map((h) => Number(h.value));

          const pointerValue = determineValue(determination, nonNullHistoryValues);

          // Skip this dataset if no valid value is available
          if (pointerValue === null) {
            return;
          }

          stateIndex++;
          if (stateIndex >= pointerColors.length) {
            stateIndex = 0;
          }

          dataSets.push({
            value: pointerValue,
            color: pointerColors[stateIndex],
            name: this.getStateLabel(twin, state.state),
            state,
            twin,
          });
        });
      });

      // If no valid datasets are available, return an empty array
      // The chart component will handle showing "No data available"
      if (dataSets.length === 0) {
        return [];
      }

      dataSets = dataSets.sort((a, b) => a.value - b.value);

      return dataSets;
    },
    // eslint-disable-next-line max-statements
    getGaugeChartOptions() {
      const gaugeChartDataSets = this.getGaugeChartDataSets();

      const min = get(this.widget.chartOptions, 'gaugeChart.min', 0);
      const max = get(this.widget.chartOptions, 'gaugeChart.max', 100);

      const detailValueFormat = get(this.widget.chartOptions, 'gaugeChart.detailValueFormat');
      const showAxisLabels = get(this.widget.chartOptions, 'gaugeChart.showAxisLabels', true);
      const showPointer = get(this.widget.chartOptions, 'gaugeChart.showPointer', true);

      let centerY = 60;
      const showLegend = get(this.widget, 'chartOptions.legend.show', true);
      if (!showLegend) {
        centerY -= 5;
      }

      const toolboxActions = get(this.widget, 'chartOptions.toolbox.actions', []);
      const showToolbox = toolboxActions.length > 0;
      if (!showToolbox) {
        centerY -= 5;
      }
      const center = ['50%', `${centerY}%`];

      let baseRadiusPercentage = 65;
      const detailFontSize = 20;

      const userDefinedThresholds = get(this.widget.chartOptions, 'gaugeChart.thresholds', []);

      const thresholds = [...userDefinedThresholds];

      if (userDefinedThresholds.length === 0 && !showAxisLabels) {
        baseRadiusPercentage += 20;
      }

      if (!thresholds.some((threshold) => threshold.value === 1)) {
        thresholds.push({ value: max, color: vuetifyColors.lightBlue.base });
      }

      if (gaugeChartDataSets.length === 1) {
        const sortedThresholds = thresholds.sort((a, b) => a.value - b.value);
        const matchingThreshold = sortedThresholds.find((threshold) => threshold.value > gaugeChartDataSets[0].value);

        if (matchingThreshold) {
          gaugeChartDataSets[0].color = matchingThreshold.color;
        }
      }

      // returns 0 to 1
      const thresholdToPercentage = (value) => {
        return (value - min) / (max - min);
      };

      const thresholdSeries = {
        min,
        max,
        zlevel: 0,
        type: 'gauge',
        radius: `${baseRadiusPercentage}%`,
        center,
        axisTick: { show: false },
        splitLine: { show: false },
        axisLabel: {
          show: showAxisLabels,
          distance: userDefinedThresholds.length > 0 ? -40 : -30,
          color: '#999',
          fontSize: 10,
        },
        axisLine: {
          show: userDefinedThresholds.length > 0,
          lineStyle: {
            width: 4,
            color: thresholds.map((threshold) => [thresholdToPercentage(threshold.value), threshold.color]),
          },
        },
        pointer: {
          show: false,
        },
        detail: { show: false },
        data: [],
      };

      const determination = get(this.widget.chartOptions, 'gaugeChart.valueDetermination', 'max');
      const detailValueDecimalPlaces = get(this.widget.chartOptions, 'gaugeChart.detailValueDecimalPlaces', 2);
      let detailValueRaw = 0;
      if (gaugeChartDataSets.length > 0) {
        detailValueRaw = determineValue(
          determination,
          gaugeChartDataSets.map((dataSet) => dataSet.value)
        );
      }

      const detailValue = detailValueRaw.toFixed(detailValueDecimalPlaces);

      const axisLineAndDetailsSeries = {
        min,
        max,
        zlevel: 1,
        radius: `${baseRadiusPercentage - 5}%`,
        type: 'gauge',
        center,
        axisTick: { show: false },
        splitLine: { show: false },
        axisLine: {
          show: true,
          lineStyle: {
            width: 40,
          },
        },
        axisLabel: { show: false },
        detail: {
          show: get(this.widget.chartOptions, 'gaugeChart.showDetailValue', true),
          valueAnimation: true,
          fontSize: detailFontSize,
          offsetCenter: [0, showPointer ? '70%' : 0],
          formatter: (animatedValue) => {
            if (!detailValueFormat) {
              return animatedValue;
            }
            return `${detailValueFormat}\n${animatedValue}`;
          },
          textStyle: {
            color: this.isDarkTheme ? '#eee' : '#333',
          },
        },
        pointer: {
          show: false,
        },
        data: [detailValue],
      };

      const dataSeries = gaugeChartDataSets.map((dataSet, indexOfDataSet) => {
        const zlevel = 2 + indexOfDataSet;
        const progressWidth =
          (axisLineAndDetailsSeries.axisLine.lineStyle.width / gaugeChartDataSets.length) *
          (gaugeChartDataSets.length - indexOfDataSet);

        return {
          // 2 + index of dataset (ordered by value)
          zlevel,
          min,
          max,
          name: dataSet.name,
          radius: `${baseRadiusPercentage - 5}%`,
          center,
          type: 'gauge',
          progress: {
            show: true,
            overlap: true,
            // axisLine Width / dataset count * index of dataset (ordered by value)
            width: progressWidth,
          },
          axisLine: {
            show: false,
          },
          axisLabel: { show: false },
          axisTick: {
            show: false,
          },
          splitLine: {
            show: false,
          },
          anchor: {
            show: showPointer,
            showAbove: true,
            size: 10,
            itemStyle: {
              borderWidth: 2,
            },
          },
          pointer: {
            show: showPointer,
            width: 3,
            length: showAxisLabels || userDefinedThresholds.length > 0 ? '40%' : '50%',
          },
          detail: { show: false },
          data: [
            {
              value: dataSet.value,
              itemStyle: {
                color: dataSet.color,
              },
            },
          ],
        };
      });

      const series = [thresholdSeries, axisLineAndDetailsSeries, ...dataSeries];

      series.push();

      let top = 30;
      if (!showToolbox) {
        top -= 15;
      }

      const option = {
        legend: {
          type: 'scroll',
          orient: 'horizontal',
          right: 0,
          top,
          left: 0,
          data: gaugeChartDataSets.map((dataSet) => {
            return {
              name: dataSet.name,
              itemStyle: { color: dataSet.color },
            };
          }),
        },
        tooltip: {},
        series,
      };

      return option;
    },
  },
};
